import { Flex, Grid, Typography, theme } from 'antd';
import { CardContent, CardShell } from '@/components/cards/common';
import { useFilterContext, useDerivedNetworkContext } from '@/context';
import { SocialChannelProfileItemEcho } from './SocialChannelProfileEcho';
import type { SocialProfile } from '@/types/api/social-profiles';
import useFetchQuery from '@/hooks/useFetchQuery';
import { useProfilesQuery } from '@/services/revenueApi';
import type { ProfilesResponse } from '@/types/api/profile';
import { ConnectProfilesMessageEcho } from './messages/ConnectProfilesMessageEcho';
import { Platform } from '@/types/api/platforms';
import { getPlatformLogoSrc } from '@/utils';
import NoDataMessageEcho from '@/components/states/NoDataMessageEcho';

const FALLBACK_SHOW_IMAGE = getPlatformLogoSrc('Acast')!;

const SocialProfilesCardEcho = ({
  className,
  hideProfileBanner,
}: {
  className?: string;
  hideProfileBanner?: boolean;
}) => {
  const { filters } = useFilterContext();
  const { queryResult } = useFetchQuery(useProfilesQuery, {
    enabled: filters.selectedShows.length > 0,
  });

  return (
    <CardShell className={className} headerClassName="mb-0">
      <CardContent
        {...queryResult}
        variant="echo"
        renderData={(profileData: ProfilesResponse) => (
          <Data
            data={profileData}
            selectedShow={filters.selectedShows[0]}
            hideProfileBanner={hideProfileBanner}
          />
        )}
      />
    </CardShell>
  );
};

const Data = ({
  data,
  selectedShow,
  hideProfileBanner,
}: {
  data: ProfilesResponse;
  selectedShow: string | undefined;
  hideProfileBanner?: boolean;
}) => {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const { selectedNetwork } = useDerivedNetworkContext();

  if (!selectedNetwork.id || !selectedShow) {
    return <NoDataMessageEcho dataType="social profile data" />;
  }

  const getFilteredProfiles = (platformName: Platform): SocialProfile[] => {
    // Filter profiles by platform and transform to SocialProfile format
    const filteredProfiles = data.profiles
      .filter((profile) => {
        // Normalize platform name comparison (case-insensitive)
        return profile.platform === platformName;
      })
      .map((profile) => {
        let handle = profile.external_id;

        if (platformName === Platform.YouTube) {
          const parts = profile.url.split('/').filter(Boolean); // last part of the URL is the handle
          handle = parts[parts.length - 1] || '';
        }

        return {
          handle,
          // Since API filters by showIds, if selectedShow is set, all profiles belong to that show
          // Otherwise, use empty string (profiles may belong to multiple shows)
          show_id: selectedShow || '',
        };
      });

    return filteredProfiles;
  };

  const acastProfiles = getFilteredProfiles(Platform.Acast);
  const youtubeProfiles = getFilteredProfiles(Platform.YouTube);
  const tiktokProfiles = getFilteredProfiles(Platform.TikTok);
  const instagramProfiles = getFilteredProfiles(Platform.Instagram);
  const totalSocialProfiles =
    youtubeProfiles.length + tiktokProfiles.length + instagramProfiles.length;
  const messageVisible = totalSocialProfiles === 0;

  return (
    <Flex align="center" style={{ width: '100%', overflow: 'hidden' }}>
      <Flex
        vertical={messageVisible && !screens.lg}
        justify={messageVisible && screens.lg ? 'space-between' : undefined}
        style={{ width: '100%' }}
      >
        <Flex
          wrap
          justify={selectedShow ? 'flex-start' : 'space-between'}
          style={{
            width: messageVisible && screens.lg ? '66.666667%' : '100%',
            columnGap: selectedShow && screens.lg ? token.marginXL : token.marginLG,
            rowGap: token.marginXL,
            padding: token.paddingLG,
          }}
        >
          {selectedShow ? (
            <Flex align="center" justify="flex-start" style={{ width: '100%' }}>
              <img
                data-testid="selected-show-image"
                src={data.show?.image_url || FALLBACK_SHOW_IMAGE}
                width="112"
                style={{
                  display: screens.sm ? 'block' : 'none',
                  height: 112,
                  marginRight: token.marginLG,
                  borderRadius: token.borderRadiusLG,
                }}
              />

              <Flex vertical gap={token.marginXS} style={{ minWidth: 0, maxWidth: '100%' }}>
                <Flex align="center" style={{ minWidth: 0 }}>
                  <img
                    data-testid="selected-show-image-sm"
                    src={data.show?.image_url || FALLBACK_SHOW_IMAGE}
                    width="32"
                    style={{
                      display: screens.sm ? 'none' : 'block',
                      height: 32,
                      marginRight: token.marginXS,
                      borderRadius: token.borderRadiusLG,
                    }}
                  />
                  <Typography.Title
                    level={2}
                    ellipsis
                    style={{
                      maxWidth: '100%',
                      margin: 0,
                      fontSize: screens.md ? 34 : token.fontSizeHeading3,
                    }}
                  >
                    {data.show?.name}
                  </Typography.Title>
                </Flex>

                <Flex gap={screens.sm ? token.marginLG : token.marginXS} style={{ minWidth: 0 }}>
                  <SocialChannelProfileItemEcho
                    type={Platform.YouTube}
                    profiles={youtubeProfiles}
                  />
                  <SocialChannelProfileItemEcho type={Platform.TikTok} profiles={tiktokProfiles} />
                  <SocialChannelProfileItemEcho
                    type={Platform.Instagram}
                    profiles={instagramProfiles}
                  />
                </Flex>
              </Flex>
            </Flex>
          ) : (
            <>
              <SocialChannelProfileItemEcho type={Platform.Acast} profiles={acastProfiles} />
              <SocialChannelProfileItemEcho type={Platform.YouTube} profiles={youtubeProfiles} />
              <SocialChannelProfileItemEcho type={Platform.TikTok} profiles={tiktokProfiles} />
              <SocialChannelProfileItemEcho
                type={Platform.Instagram}
                profiles={instagramProfiles}
              />
            </>
          )}
        </Flex>
        {selectedShow && totalSocialProfiles === 0 && !hideProfileBanner && (
          <ConnectProfilesMessageEcho networkId={selectedNetwork.id} showId={selectedShow} />
        )}
      </Flex>
    </Flex>
  );
};

export default SocialProfilesCardEcho;
